import type { UserRoleType } from "@/types/auth";

type PermissionAction = "create" | "read" | "update" | "delete";
type EntityType =
  | "users"
  | "contacts"
  | "clients"
  | "projects"
  | "roles"
  | "shifts"
  | "shift_users"
  | "shift_positions"
  | "time_cards"
  | "training"
  | "courses"
  | "invoices"
  | "estimates"
  | "expenses"
  | "incidents"
  | "investigations"
  | "applications"
  | "tenant"
  | "user_emergency_contacts"
  | "user_private"
  | "user_pay"
  | "user_reviews"
  | "locations"
  | "unions"
  | "markets"
  | "payroll"
  | "positions"
  | "abilities"
  | "restrictions"
  | "dress_codes"
  | "billing_items"
  | "incident_types";

/**
 * Check if user has any role granting the admin portal
 */
export function hasAdminPortal(userRoles: UserRoleType[] | null): boolean {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }

  return userRoles.some((role) => {
    return role.permissions?.admin_portal === true;
  });
}
/**
 * Check if user has specific permission for an entity
 */
export function hasPermission(userRoles: UserRoleType[] | null, entity: EntityType, action: PermissionAction): boolean {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }

  return userRoles.some((role) => {
    if (!role.permissions) {
      return false;
    }

    const entityPermissions = role.permissions[entity];

    // Handle CRUD permissions structure
    if (typeof entityPermissions === "object" && entityPermissions !== null) {
      return entityPermissions[action] === true;
    }

    return false;
  });
}

/**
 * Get all permissions for an entity across all user roles
 */
export function getEntityPermissions(
  userRoles: UserRoleType[] | null,
  entity: EntityType,
): {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
} {
  return {
    create: hasPermission(userRoles, entity, "create"),
    read: hasPermission(userRoles, entity, "read"),
    update: hasPermission(userRoles, entity, "update"),
    delete: hasPermission(userRoles, entity, "delete"),
  };
}

/**
 * Get table action permissions based on user roles
 */
export function getTablePermissions(userRoles: UserRoleType[] | null, entity: EntityType) {
  const permissions = getEntityPermissions(userRoles, entity);

  return {
    enableCreate: permissions.create,
    enableSave: permissions.update,
    enableDelete: permissions.delete,
  };
}

/**
 * Filter table columns based on relationship permissions.
 * Columns that reference entities the user can't read will be removed.
 *
 * @param userRoles - User's roles with permissions
 * @param columns - Array of column definitions with 'name' property
 * @param relationshipMap - Map of column names to required entity permissions
 * @returns Filtered columns array
 *
 * @example
 * const columns = filterColumnsByPermission(
 *   userRoles,
 *   filterChoices,
 *   { client: "clients", project: "projects", user: "users" }
 * );
 */
export function filterColumnsByPermission<T extends { name: string }>(
  userRoles: UserRoleType[] | null,
  columns: T[],
  relationshipMap: Partial<Record<string, EntityType>>,
): T[] {
  return columns.filter((col) => {
    const requiredEntity = relationshipMap[col.name];
    if (!requiredEntity) return true; // No permission required for this column
    return hasPermission(userRoles, requiredEntity, "read");
  });
}

/**
 * Get columns to ignore based on relationship permissions.
 * Returns column names that should be hidden because user lacks read access.
 *
 * @param userRoles - User's roles with permissions
 * @param relationshipMap - Map of column names to required entity permissions
 * @returns Array of column names to hide
 *
 * @example
 * const hiddenColumns = getIgnoredColumnsByPermission(
 *   userRoles,
 *   { client: "clients", project: "projects" }
 * );
 * // Returns ["client"] if user doesn't have clients.read
 */
export function getIgnoredColumnsByPermission(
  userRoles: UserRoleType[] | null,
  relationshipMap: Record<string, EntityType>,
): string[] {
  return Object.entries(relationshipMap)
    .filter(([_, entity]) => !hasPermission(userRoles, entity, "read"))
    .map(([columnName]) => columnName);
}
